import { useSyncExternalStore } from 'react';
import type { Track, Video } from '@blackblack/shared';

/**
 * Normalised overrides for interactive entity state (likes, saves, follows).
 * Any list or page shows the latest state after an optimistic mutation without
 * refetching — e.g. a like in the feed is reflected in the profile grid.
 */
type Patch = Record<string, unknown>;
const patches = new Map<string, Patch>();
const listeners = new Set<() => void>();
let version = 0;

export function patchEntity(kind: 'video' | 'user' | 'track' | 'playlist', id: string, patch: Patch) {
  const key = `${kind}:${id}`;
  patches.set(key, { ...patches.get(key), ...patch });
  version++;
  listeners.forEach((l) => l());
}

export function getPatch(kind: string, id: string): Patch | undefined {
  return patches.get(`${kind}:${id}`);
}

function useVersion() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
  );
}

export function useVideo(v: Video): Video {
  useVersion();
  const p = patches.get(`video:${v.id}`) as Partial<{ liked: boolean; likes: number; saved: boolean; saves: number; comments: number; shares: number }> | undefined;
  const u = patches.get(`user:${v.author.id}`) as Partial<{ following: boolean }> | undefined;
  if (!p && !u) return v;
  return {
    ...v,
    stats: { ...v.stats, likes: p?.likes ?? v.stats.likes, saves: p?.saves ?? v.stats.saves, comments: p?.comments ?? v.stats.comments, shares: p?.shares ?? v.stats.shares },
    viewer: { liked: p?.liked ?? v.viewer.liked, saved: p?.saved ?? v.viewer.saved, followingAuthor: u?.following ?? v.viewer.followingAuthor },
  };
}

export function useTrack<T extends Pick<Track, 'id' | 'liked'>>(t: T): T {
  useVersion();
  const p = patches.get(`track:${t.id}`) as Partial<{ liked: boolean }> | undefined;
  return p ? { ...t, liked: p.liked ?? t.liked } : t;
}

export function useFollowing(userId: string, base: boolean): boolean {
  useVersion();
  return (patches.get(`user:${userId}`)?.following as boolean | undefined) ?? base;
}

export function clearEntityPatches() {
  patches.clear();
  version++;
  listeners.forEach((l) => l());
}
